import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState, useEffect } from "react";
import {
  copiarTipologiasDeOutroCaso,
  ICopiarCasoRequest,
  listarCasosPorAtividade,
  ICaso,
  obterAtividadeBasica,
} from "services/modules/tarefaDetalhe";
import { useNavigation } from "contexts/notificationsContext";

// ============================================
// INTERFACES
// ============================================

interface ModalCopiarCasosProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefaCaso: number;
  idTarefaAtividade: number;
  onCasosCopiados: () => void;
  // 24/08 (pedido do PO): em Caso NOVO nada grava até o SALVAR — quando informado,
  // a confirmação devolve o caso de origem escolhido em vez de chamar a API.
  onSelecionarSemSalvar?: (casoOrigem: { idTarefaCaso: number; descricao: string }) => void;
}

// ============================================
// COMPONENTE
// ============================================

export const ModalCopiarCasos = ({
  isOpen,
  onClose,
  idTarefaCaso,
  idTarefaAtividade,
  onCasosCopiados,
  onSelecionarSemSalvar,
}: ModalCopiarCasosProps) => {
  const { openNotification } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [loadingCasos, setLoadingCasos] = useState(false);
  const [casos, setCasos] = useState<ICaso[]>([]);
  const [casoSelecionado, setCasoSelecionado] = useState<ICaso | null>(null);

  // Carregar casos da mesma atividade
  const carregarCasos = async () => {
    setLoadingCasos(true);
    try {
      console.log("[ModalCopiarCasos] Iniciando carregamento de casos. idTarefaAtividade:", idTarefaAtividade);
      console.log("[ModalCopiarCasos] idTarefaCaso (caso atual):", idTarefaCaso);
      
      // Verificar se a atividade existe
      const atividadeBasica = await obterAtividadeBasica(idTarefaAtividade);
      
      console.log("[ModalCopiarCasos] Atividade básica obtida:", atividadeBasica);
      
      if (!atividadeBasica) {
        console.error("[ModalCopiarCasos] Atividade não encontrada para idTarefaAtividade:", idTarefaAtividade);
        openNotification({
          isOpen: true,
          message: "Atividade não encontrada",
          type: "error",
        });
        setCasos([]);
        return;
      }

      // Buscar TODOS os casos da mesma atividade (incluindo o caso atual)
      console.log(`[ModalCopiarCasos] Buscando casos da atividade ${idTarefaAtividade} (${atividadeBasica.titulo})`);
      const todosCasos = await listarCasosPorAtividade(idTarefaAtividade);
      
      console.log("[ModalCopiarCasos] Total de casos encontrados na atividade:", todosCasos.length);
      console.log("[ModalCopiarCasos] Casos brutos:", todosCasos);

      // Filtrar apenas casos ativos e diferentes do caso atual
      const casosFiltrados = todosCasos.filter(
        (c) => {
          const isAtivo = c.ativo !== false; // Considerar undefined/null como ativo
          const isDiferente = c.idTarefaCaso !== idTarefaCaso;
          console.log(`[ModalCopiarCasos] Caso ${c.idTarefaCaso}: ativo=${isAtivo}, diferente=${isDiferente}, será incluído=${isAtivo && isDiferente}`);
          return isAtivo && isDiferente;
        }
      );

      console.log("[ModalCopiarCasos] Total de casos após filtro:", casosFiltrados.length);
      console.log("[ModalCopiarCasos] Casos filtrados:", casosFiltrados);

      setCasos(casosFiltrados);
    } catch (error: any) {
      console.error("[ModalCopiarCasos] Erro ao carregar casos:", error);
      console.error("[ModalCopiarCasos] Detalhes do erro:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      openNotification({
        isOpen: true,
        message: error.response?.data?.message || "Erro ao carregar casos relacionados. Verifique o console para mais detalhes.",
        type: "error",
      });
      setCasos([]);
    } finally {
      setLoadingCasos(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      carregarCasos();
      setCasoSelecionado(null);
    }
  }, [isOpen, idTarefaAtividade, idTarefaCaso]);

  const handleSelecionarCaso = (caso: ICaso) => {
    setCasoSelecionado(caso);
  };

  const handleImportar = async () => {
    if (!casoSelecionado) {
      openNotification({
        isOpen: true,
        message: "Selecione um caso para copiar",
        type: "warning",
      });
      return;
    }

    // 24/08: Caso novo — devolve o caso de origem; a cópia ocorre no SALVAR do Caso.
    if (onSelecionarSemSalvar) {
      onSelecionarSemSalvar({
        idTarefaCaso: casoSelecionado.idTarefaCaso,
        descricao: casoSelecionado.descricao || "",
      });
      openNotification({
        isOpen: true,
        message: `Tipologias do Caso ${casoSelecionado.idTarefaCaso} adicionadas — serão gravadas ao salvar o Caso.`,
        type: "info",
      });
      onClose();
      return;
    }

    setLoading(true);
    try {
      const request: ICopiarCasoRequest = {
        idTarefaCasoOrigem: casoSelecionado.idTarefaCaso,
        idTarefaCasoDestino: idTarefaCaso,
      };

      await copiarTipologiasDeOutroCaso(idTarefaCaso, request);

      openNotification({
        isOpen: true,
        message: `Tipologias do Caso ${casoSelecionado.idTarefaCaso} copiadas com sucesso!`,
        type: "success",
      });

      onCasosCopiados();
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao copiar tipologias",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Casos relacionados a atividade</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Cadastre todas as tipologias que farão parte do caso
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Os Casos apresentados para seleção são apenas aqueles que existem na mesma atividade.
          Todos os dados do Caso selecionado serão copiados para o novo Caso.
        </Alert>

        {loadingCasos ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : casos.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              border: "1px dashed #ccc",
              borderRadius: 1,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              Nenhum caso encontrado na mesma atividade
            </Typography>
          </Box>
        ) : (
          <Paper variant="outlined">
            <List>
              {casos.map((caso) => (
                <ListItem
                  key={caso.idTarefaCaso}
                  disablePadding
                  secondaryAction={
                    <Button
                      variant={casoSelecionado?.idTarefaCaso === caso.idTarefaCaso ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleSelecionarCaso(caso)}
                      sx={{ textTransform: "none" }}
                    >
                      SELECIONAR
                    </Button>
                  }
                >
                  <ListItemButton
                    selected={casoSelecionado?.idTarefaCaso === caso.idTarefaCaso}
                    onClick={() => handleSelecionarCaso(caso)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={500}>
                          Caso {caso.idTarefaCaso}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {caso.descricao || "Sem descrição"}
                          </Typography>
                          {caso.tipologias && caso.tipologias.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                              {caso.tipologias.length} tipologia(s)
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {casoSelecionado && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: "action.selected", borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={500} gutterBottom>
              Caso selecionado:
            </Typography>
            <Typography variant="body2">
              <strong>Caso {casoSelecionado.idTarefaCaso}:</strong> {casoSelecionado.descricao}
            </Typography>
            {casoSelecionado.tipologias && casoSelecionado.tipologias.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {casoSelecionado.tipologias.length} tipologia(s) serão copiadas
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleImportar}
          variant="contained"
          color="primary"
          disabled={loading || !casoSelecionado || loadingCasos}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading ? <CircularProgress size={20} /> : "IMPORTAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

